export function getProperty(feature, key) {
  return (
    feature.get(key) ||
    feature.get(key + ":step") ||
    getPropertyEndingWith(feature, "-" + key)
  );
}
// Also matches the `:step` form of the key. A tag whose value is sectioned
// ("0-1000#yes;5000-7000#no") is stored under `<key>:step` — see WFSLayer's
// renameSectionedTagsToLRStep — and callers ask for the bare name.
export function getPropertyEndingWith(feature, keySuffix) {
  const key = feature
    .getKeys()
    .filter(
      (key) => key.endsWith(keySuffix) || key.endsWith(keySuffix + ":step"),
    )
    .filter((key) => !key.startsWith("revision-"))[0];
  return feature.get(key);
}

export function getPropertyEndingWithExcludingLayer7(feature, keySuffix) {
  const key = feature
    .getKeys()
    .filter((key) => !key.startsWith("7-"))
    .filter((key) => !key.startsWith("osm-"))
    .filter((key) => !key.startsWith("revision-"))
    .filter(
      (key) => key.endsWith(keySuffix) || key.endsWith(keySuffix + ":step"),
    )[0];
  return feature.get(key);
}

// Accepts a precompiled RegExp as well as a pattern string. extractTags runs this
// 16 times per feature per render frame across eight layers, so the new callers pass
// module-level RegExps rather than rebuilding one each time.
export function getPropertyMatching(feature, regExpOrString) {
  const regExp =
    typeof regExpOrString === "string"
      ? new RegExp(regExpOrString)
      : regExpOrString;
  const key = feature.getKeys().filter((key) => regExp.test(key))[0];
  return feature.get(key);
}

// New-spec per-lane tag keys. Keys arrive layer-id-prefixed (`14906-lanes`), and a way
// carries the parent basemap's tags alongside the child layer's — so both patterns
// exclude the basemap prefixes (6 = SD/OSM basemap, 7 = ETR; both are parents of the
// osm-laneres WFS). `lanes` is an established OSM key, so the raw `6-lanes` really can
// sit beside the normalized `14906-lanes`, and getPropertyMatching returns whichever
// getKeys() lists first — a silent coin flip on WFS property order. `lanes:direction` is
// not a standard OSM key, but it is excluded too rather than relying on that.
//
// The trailing `.*` absorbs the `:step` and `:conditional` suffixes; unlike
// getProperty(), getPropertyMatching() has no built-in `:step` awareness.
const NOT_BASEMAP = "^(?!6-|7-)\\d+-";
const LANES_DIRECTION = new RegExp(NOT_BASEMAP + "lanes:direction.*");
// Anchored, unlike the one above: unanchored it would also swallow `lanes:direction`
// and `lanes:vehicle_access:*`.
const LANES_TOTAL = new RegExp(NOT_BASEMAP + "lanes(:step)?$");
// Per-direction lane counts, for the lane-count view. Anchored for the same reason.
const LANES_FORWARD = new RegExp(NOT_BASEMAP + "lanes:forward(:step)?$");
const LANES_BACKWARD = new RegExp(NOT_BASEMAP + "lanes:backward(:step)?$");
const LANES_BOTH_WAYS = new RegExp(NOT_BASEMAP + "lanes:both_ways(:step)?$");
// `hov:lanes` and its #Conditional sibling are SEPARATE extractions, both anchored.
// The old `^\d+-hov:lanes.*` matched both keys at once, so a way carrying both would
// resolve to whichever getKeys() listed first. The `(:step)?` covers the sectioned
// form — WFSLayer's renameSectionedTagsToLRStep re-appends `:step` to any key whose
// value decodes as a step chain, the conditional keys included.
const HOV_LANES = new RegExp("^\\d+-hov:lanes(:step)?$");
const HOV_LANES_CONDITIONAL = new RegExp(
  "^\\d+-hov:lanes:conditional(:step)?$",
);
const HOV_MINIMUM_LANES_CONDITIONAL = new RegExp(
  "^\\d+-hov:minimum:lanes:conditional(:step)?$",
);
// Anchored for the same reason: the old trailing `.*` also matched
// `lanes:vehicle_access:<mode>:conditional`, so a conditional ban read as a plain one.
// Conditionals are not read — the LM<->RM mapping lib reads one as "unknown", which
// never colours a lane.
const vehicleAccess = (mode) =>
  new RegExp(`^\\d+-lanes:vehicle_access:${mode}(:step)?$`);
const VEHICLE_ACCESS_BUS = vehicleAccess("bus");
const VEHICLE_ACCESS_TAXI = vehicleAccess("taxi");
const VEHICLE_ACCESS_MOTORCAR = vehicleAccess("motorcar");
const VEHICLE_ACCESS_HGV = vehicleAccess("hgv");

/**
 * @typedef {Object} Tags
 * @property {ReturnType<typeof getPropertyEndingWith>} hdMap
 * @property {ReturnType<typeof getPropertyEndingWith>} laneDirection
 * @property {ReturnType<typeof getPropertyEndingWith>} laneFunction
 * @property {ReturnType<typeof getPropertyEndingWith>} hovLane
 * @property {ReturnType<typeof getPropertyEndingWith>} hov
 * @property {ReturnType<typeof getPropertyEndingWith>} vehicleRestrictionBus
 * @property {ReturnType<typeof getPropertyEndingWith>} vehicleRestrictionHGV
 * @property {ReturnType<typeof getPropertyEndingWith>} vehicleRestrictionMotorVehicle
 * @property {ReturnType<typeof getPropertyEndingWith>} vehicleRestrictionMotorcar
 * @property {ReturnType<typeof getPropertyEndingWith>} vehicleRestrictionPSV
 * @property {ReturnType<typeof getPropertyEndingWith>} vehicleRestrictionTaxi
 * @property {ReturnType<typeof getPropertyEndingWith>} laneBorder
 * @property {ReturnType<typeof getPropertyMatching>} lanesNormMotorVehicle
 * @property {ReturnType<typeof getPropertyMatching>} lanesNormMotorCar
 * @property {ReturnType<typeof getPropertyMatching>} lanesNormHgv
 * @property {ReturnType<typeof getPropertyMatching>} lanesNormPsv
 * @property {ReturnType<typeof getPropertyMatching>} lanesNormBus
 * @property {ReturnType<typeof getPropertyMatching>} lanesNormTaxi
 * @property {ReturnType<typeof getPropertyMatching>} lanesNormHov
 * @property {ReturnType<typeof getPropertyMatching>} rm_hovLanes
 * @property {ReturnType<typeof getPropertyMatching>} rm_hovLanesConditional
 * @property {ReturnType<typeof getPropertyMatching>} rm_hovMinimumLanesConditional
 * @property {ReturnType<typeof getPropertyMatching>} rm_vehicleAccessBus
 * @property {ReturnType<typeof getPropertyMatching>} rm_vehicleAccessTaxi
 * @property {ReturnType<typeof getPropertyMatching>} rm_vehicleAccessMotorcar
 * @property {ReturnType<typeof getPropertyMatching>} rm_vehicleAccessHgv
 * @property {ReturnType<typeof getPropertyMatching>} rm_lanesDirection
 * @property {ReturnType<typeof getPropertyMatching>} rm_lanesTotal
 * @property {ReturnType<typeof getPropertyMatching>} rm_lanesForward
 * @property {ReturnType<typeof getPropertyMatching>} rm_lanesBackward
 * @property {ReturnType<typeof getPropertyMatching>} rm_lanesBothWays
 */

/**
 * Extracts properties from a feature.
 * @param {any} feature - The feature object.
 * @returns {Tags} The extracted tag properties.
 */
export function extractTags(feature) {
  /** @type {Tags} */
  const tags = {
    hdMap: getProperty(feature, "hdmap"),
    laneDirection: getProperty(feature, "lane_direction"),
    laneFunction: getProperty(feature, "lane_function"),
    rm_hovLanes: getPropertyMatching(feature, HOV_LANES),
    rm_hovLanesConditional: getPropertyMatching(feature, HOV_LANES_CONDITIONAL),
    rm_hovMinimumLanesConditional: getPropertyMatching(
      feature,
      HOV_MINIMUM_LANES_CONDITIONAL,
    ),
    rm_vehicleAccessBus: getPropertyMatching(feature, VEHICLE_ACCESS_BUS),
    rm_vehicleAccessTaxi: getPropertyMatching(feature, VEHICLE_ACCESS_TAXI),
    rm_vehicleAccessMotorcar: getPropertyMatching(
      feature,
      VEHICLE_ACCESS_MOTORCAR,
    ),
    rm_vehicleAccessHgv: getPropertyMatching(feature, VEHICLE_ACCESS_HGV),
    rm_lanesDirection: getPropertyMatching(feature, LANES_DIRECTION),
    rm_lanesTotal: getPropertyMatching(feature, LANES_TOTAL),
    rm_lanesForward: getPropertyMatching(feature, LANES_FORWARD),
    rm_lanesBackward: getPropertyMatching(feature, LANES_BACKWARD),
    rm_lanesBothWays: getPropertyMatching(feature, LANES_BOTH_WAYS),
    hovLane: getProperty(feature, "hov_lane"),
    hov: getProperty(feature, "hov"),
    vehicleRestrictionBus: getProperty(feature, "vehicle_restriction:bus"),
    vehicleRestrictionHGV: getProperty(feature, "vehicle_restriction:hgv"),
    vehicleRestrictionMotorVehicle: getProperty(
      feature,
      "vehicle_restriction:motor_vehicle",
    ),
    vehicleRestrictionMotorcar: getProperty(
      feature,
      "vehicle_restriction:motorcar",
    ),
    vehicleRestrictionPSV: getProperty(feature, "vehicle_restriction:psv"),
    vehicleRestrictionTaxi: getProperty(feature, "vehicle_restriction:taxi"),
    laneBorder: getProperty(feature, "lane_border"),
    lanesNormMotorVehicle: getPropertyMatching(
      feature,
      "^(?!7-|osm-|revision-).*-lanes:motor_vehicle",
    ),
    lanesNormMotorCar: getPropertyMatching(
      feature,
      "^(?!7-|osm-|revision-).*-lanes:motor_car",
    ),
    lanesNormHgv: getPropertyMatching(
      feature,
      "^(?!7-|osm-|revision-).*-lanes:hgv",
    ),
    lanesNormPsv: getPropertyMatching(
      feature,
      "^(?!7-|osm-|revision-).*-lanes:psv",
    ),
    lanesNormBus: getPropertyMatching(
      feature,
      "^(?!7-|osm-|revision-).*-lanes:bus",
    ),
    lanesNormTaxi: getPropertyMatching(
      feature,
      "^(?!7-|osm-|revision-).*-lanes:taxi",
    ),
    lanesNormHov: getPropertyMatching(
      feature,
      "^(?!7-|osm-|revision-).*-lanes:hov",
    ),
  };
  return tags;
}
